import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { DacGeneralParamsRepository } from '@/store/dac-general-params-state/dac-general-params-state.repository';
import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { DacGeneralParamsService } from '../dac-general-params.service';
import { DynaDocsModelStatusService } from '../dyna-docs-model-status.service';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { DynamicForm, SelectOption } from '@/components/form-builder/form-builder.types';
import { BlockVariant, DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { getEditDacGeneralParamsFormObject } from './utils/edit-dac-general-params.form-object';
import { dynaDocsModelStatusStoreFactory } from '@/store/dac-general-params-state/dyna-docs-model-status-state/dyna-docs-model-status.store';
import { ParametrageGeneralDacDto, ParamGeneralType } from '@/types';
import { paramGeneralBaseSchema } from '@/validations/dac-general-params.validation';
import { systemTypeOptions, systemSubTypes } from '@/store/dac-general-params-state/param-general-field-state/param-general-field.repository';

@Component({
  selector: 'sm-edit-dac-general-params',
  standalone: true,
  imports: [PageHeaderComponent, FormBuilderComponent, BlockBuilderComponent, SpinnerComponent, AsyncPipe],
  templateUrl: './edit-dac-general-params.component.html',
})
export class EditDacGeneralParamsComponent implements OnInit, OnDestroy {
  dacGeneralParamsRepository = inject(DacGeneralParamsRepository);
  multiFormAccordionService = inject(MultiFormAccordionService);
  dacGeneralParamsService = inject(DacGeneralParamsService);
  dynaDocsModelStatusService = inject(DynaDocsModelStatusService);
  messageService = inject(MessageService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  translate = inject(TranslateService);

  id = this.activatedRoute.snapshot.params['id'] as number;

  breadcrumb = [
    { label: 'Paramètrage', routerLink: ['/settings'] },
    { label: 'dacGeneralParams.title', routerLink: ['/settings', 'general-parameters'] },
    { label: 'dacGeneralParams.pages.edit' },
  ];

  title = 'dacGeneralParams.pages.edit';

  loading$ = new BehaviorSubject<boolean>(false);
  dynaDocsModelOptions: SelectOption[] = [];

  formObject: DynamicForm = getFormBuilderObjectFactory();

  controlBlocks: DynamicBlocks = {
    '1': [
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'common.update',
          onClick: () => this.save(),
          loading: this.loading$,
        },
      },
    ],
    '2': [
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'common.cancel',
          onClick: () => this.router.navigate(['/settings', 'general-parameters']),
          severity: 'secondary',
        },
      },
    ],
  };

  ngOnInit() {
    this.loadData();
  }

  ngOnDestroy() {
    this.multiFormAccordionService.reset(0);
    this.dacGeneralParamsRepository.reset();
  }

  loadData() {
    this.loading$.next(true);
    this.multiFormAccordionService.formFactory = (id: string) => dynaDocsModelStatusStoreFactory(id);
    this.dynaDocsModelStatusService.initialize();

    forkJoin({
      dynaDocsModels: this.dacGeneralParamsService.getDynaDocsModels(),
      data: this.dacGeneralParamsService.getOne(this.id),
    }).subscribe({
      next: ({ dynaDocsModels, data }) => {
        this.dynaDocsModelOptions = dynaDocsModels.map(dm => ({ name: dm.description, code: dm.dynaDocsModelId }));

        const response = data;
        this.dacGeneralParamsRepository.set('response', response);

        // Map the base fields into the updateDto
        this.dacGeneralParamsRepository.setNested('updateDto', 'paramGeneralName', response.paramGeneralName);
        this.dacGeneralParamsRepository.setNested('updateDto', 'paramGeneralLabel', response.paramGeneralLabel);
        this.dacGeneralParamsRepository.setNested('updateDto', 'paramGeneralType', response.paramGeneralType);
        this.dacGeneralParamsRepository.setNested('updateDto', 'paramGeneralOrder', response.paramGeneralOrder);
        this.dacGeneralParamsRepository.setNested('updateDto', 'paramGeneralDescription', response.paramGeneralDescription);
        this.dacGeneralParamsRepository.setNested('updateDto', 'paramGeneralSource', response.paramGeneralSource);
        this.dacGeneralParamsRepository.setNested('updateDto', 'paramGeneralSourceField', response.paramGeneralSourceField);

        this.dacGeneralParamsRepository.set('paramGeneralTypeSelected', {
          name: `dacGeneralParams.types.${response.paramGeneralType}`,
          code: response.paramGeneralType,
        });

        // Set source fields if type is SYSTEM
        if (response.paramGeneralType === ParamGeneralType.SYSTEM && response.paramGeneralSource) {
          const sourceOption = systemTypeOptions.find(opt => opt.code === response.paramGeneralSource);
          if (sourceOption) {
            this.dacGeneralParamsRepository.set('sourceSelected', sourceOption);
          }
          if (response.paramGeneralSourceField) {
            const subTypes = systemSubTypes[response.paramGeneralSource] || [];
            const sourceFieldOption = subTypes.find(st => st.value === response.paramGeneralSourceField);
            if (sourceFieldOption) {
              this.dacGeneralParamsRepository.set('sourceFieldSelected', { name: sourceFieldOption.label, code: sourceFieldOption.value });
            }
          }
        }

        // Initialize MFA with empty root then load models
        this.multiFormAccordionService.initStructure({ children: [] });
        if (response.dynaDocsModels && response.dynaDocsModels.length > 0) {
          this.dynaDocsModelStatusService.setFieldDtos(response.dynaDocsModels);
        } else {
          this.multiFormAccordionService.init();
        }

        this.formObject = getEditDacGeneralParamsFormObject({
          store: this.dacGeneralParamsRepository,
          dynaDocsModelOptions: this.dynaDocsModelOptions,
        });

        this.loading$.next(false);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error?.message || 'Erreur lors du chargement',
        });
        this.loading$.next(false);
      },
    });
  }

  save() {
    const dto = this.dacGeneralParamsRepository.get<ParametrageGeneralDacDto>('updateDto');

    // Validate base fields
    const baseResult = paramGeneralBaseSchema.safeParse(dto);

    // Validate models
    const modelsValid = this.dynaDocsModelStatusService.validateAllFieldDtos();

    if (baseResult.success && modelsValid) {
      const modelDtos = this.dynaDocsModelStatusService.getAllFieldDtos();

      const payload: ParametrageGeneralDacDto = {
        ...dto,
        dynaDocsModels: modelDtos as any,
      };

      this.loading$.next(true);
      this.dacGeneralParamsService.update(this.id, payload).subscribe({
        next: () => {
          this.multiFormAccordionService.reset(0);
          this.dacGeneralParamsRepository.reset();
          this.router.navigate(['/settings', 'general-parameters']);
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('dacGeneralParams.update.success'),
          });
          this.loading$.next(false);
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error?.message || this.translate.instant('dacGeneralParams.update.error'),
          });
          this.loading$.next(false);
        },
      });
    } else if (baseResult.error) {
      this.dacGeneralParamsRepository.setErrors(baseResult.error.flatten().fieldErrors);
    }
  }
}
